import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { router } from '@inertiajs/react';
import { BookOpen, MessageCircle, CheckCircle, Clock, Plus, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { logError } from '@/lib/logger';

interface DailyActionsCardProps {
  todayJournal: {
    status: 'completed' | 'in_progress' | 'not_started';
    id?: number;
    chat_id?: number;
  };
}

export function DailyActionsCard({ todayJournal }: DailyActionsCardProps) {
  const [loading, setLoading] = useState(false);

  const journalStatus = (() => {
    switch (todayJournal.status) {
      case 'completed':
        return {
          label: 'Completato',
          variant: 'default' as const,
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-50 hover:bg-green-100',
          action: 'Visualizza diario',
        };
      case 'in_progress':
        return {
          label: 'In corso',
          variant: 'secondary' as const,
          icon: Clock,
          color: 'text-orange-600',
          bgColor: 'bg-orange-50 hover:bg-orange-100',
          action: 'Continua diario',
        };
      default:
        return {
          label: 'Da completare',
          variant: 'outline' as const,
          icon: Plus,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50 hover:bg-blue-100',
          action: 'Compila diario',
        };
    }
  })();

  const handleJournalAction = async () => {
    if (loading) return;

    if (todayJournal.status === 'completed' && todayJournal.id) {
      router.get(`/patients/journals/${todayJournal.id}`);
      return;
    }

    try {
      setLoading(true);
      await new Promise<void>((resolve, reject) => {
        router.visit('/patients/journals/start', {
          method: 'post',
          preserveScroll: true,
          replace: true,
          onSuccess: () => resolve(),
          onError: () => reject(new Error('start_failed')),
          onFinish: () => setLoading(false),
        });
      });
    } catch (error) {
      logError('Errore avvio diario', error);
      window.alert('Impossibile avviare il diario. Riprova più tardi.');
      setLoading(false);
    }
  };

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
            <BookOpen className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          Azioni del giorno
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <journalStatus.icon className={cn('h-5 w-5', journalStatus.color)} />
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">Diario Giornaliero</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Aggiungi una pagina al tuo diario
                </p>
              </div>
            </div>
            <Badge variant={journalStatus.variant}>{journalStatus.label}</Badge>
          </div>

          <Button
            onClick={handleJournalAction}
            disabled={loading}
            className={cn(
              'w-full',
              journalStatus.bgColor,
              'border border-gray-200 text-gray-900 hover:text-gray-900',
              'dark:border-gray-700 dark:text-white dark:hover:text-white',
            )}
            variant="outline"
            size="lg"
          >
            {loading ? (
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            ) : (
              <journalStatus.icon className="mr-2 h-5 w-5" />
            )}
            {loading ? 'Avvio…' : journalStatus.action}
          </Button>
        </div>

        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <MessageCircle className="h-5 w-5 text-purple-600" />
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">Chat di supporto</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Conversa con il tuo assistente AI
                </p>
              </div>
            </div>
          </div>

          <Button
            onClick={() => router.get('/patients/chats/new')}
            className="w-full bg-purple-50 hover:bg-purple-100 border border-purple-200 text-purple-700 hover:text-purple-800 dark:bg-purple-950/20 dark:border-purple-800 dark:text-purple-300 dark:hover:text-purple-200"
            variant="outline"
            size="lg"
          >
            <MessageCircle className="mr-2 h-5 w-5" />
            Inizia conversazione
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
